import React, { useState, useEffect } from "react";
import {
  ArrowLeft,
  Eye,
  Plus,
  Trash2,
  Edit2,
  X,
  ChevronDown,
  ChevronUp,
  Package,
  Info,
  FileText,
  CalendarDays,
  Mail,
} from "lucide-react";
import { useParams, useNavigate, Link } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import http from "../config/http";
import HomeIcon from "../assets/helperIcons/HomeIcon";
import EditIcon from "../assets/helperIcons/EditIcon";
import RightIcon from "../assets/helperIcons/RightIcon";
import CustomDatePicker from "../components/CustomDatePicker";
import CalenderIcon from "../assets/helperIcons/CalendarIcon";
import ArrowDownIcon from "../assets/helperIcons/ArrowDownIcon";
import dayjs from "dayjs";
import SendIcon from "../assets/helperIcons/SendIcon";
import SaveIcon from "../assets/helperIcons/SaveIcon";
import CalenderBigIcon from "../assets/helperIcons/CalenderBigIcon";
import DateTimeIcon from "../assets/helperIcons/DateTimeIcon";
import ClockIcon from "../assets/helperIcons/ClockIcon";
import CalenderCheckIcon from "../assets/helperIcons/CalenderCheckIcon";
import ServiceIcon from "../assets/helperIcons/ServiceIcon";
import DeleteIcon from "../assets/helperIcons/DeleteIcon";
import CheckRoundIcon from "../assets/helperIcons/CheckRoundIcon";
import CustomDropdown from "../components/CustomDropdown";
import QuoteItemsTable from "../components/QuoteItemsTable";
import AdditionalInfoIcon from "../assets/helperIcons/AdditionalInfoIcon";
import ResizeIcon from "../assets/helperIcons/ResizeIcon";
import ContactInfoIcon from "../assets/helperIcons/ContactInfoIcon";
import LinkIcon from "../assets/helperIcons/LinkIcon";
import InvoiceSummaryIcon from "../assets/helperIcons/InvoiceSummaryIcon";
import { useSettings } from "../contexts/SettingsContext";
import AddCustomerModal from "../components/AddCustomerModal";
import DynamicFieldsEditor, { fieldsForModule } from "../components/shared/DynamicFieldsEditor";
import PaymentPlanEditor, { emptyPaymentPlan, paymentPlanPayload, validatePaymentPlan } from "../components/PaymentPlanEditor";
import SearchableSelect from "../components/SearchableSelect";
import { isRealVenueName, sanitizeVenueNames } from "../utils/venues";

export default function QuoteDetailPage() {
  const today = dayjs().format("YYYY-MM-DD");
  const { settings } = useSettings();
  const eventTypes = settings?.eventTypes || [];
  const venueNames = sanitizeVenueNames(settings?.venueNames);
  const quoteCustomFields = fieldsForModule(settings?.customFields, "event");
  const { id } = useParams();
  const navigate = useNavigate();
  const isNewQuote = !id || id === "new";

  // Loading states
  const [loading, setLoading] = useState(!isNewQuote);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("villa");
  const [customers, setCustomers] = useState([]);
  const [showCustomerModal, setShowCustomerModal] = useState(false);

  // State for quote data with embedded event information
  const [quote, setQuote] = useState({
    quoteNumber: "",
    quoteDate: today,
    expiryDate: today,
    customerId: "",
    customerName: "",
    customerEmail: "",
    billingAddress: "",
    status: "draft",
    taxRate: 16,
    taxName: "IVA",
    notes: "",
    terms: "",
    subtotal: 0,
    taxAmount: 0,
    total: 0,
    couponId: "",
    couponDiscount: 0,
    eventId: "",
    eventName: "",
    customFields: {},
    template: "villa",
    // Event information embedded in quote
    eventData: {
      eventDate: "",
      eventTime: "",
      eventType: "",
      guests: "",
      venue: "",
    },
  });

  const taxOptions = [
    { value: 0, label: "No Tax (0%)", name: "No Tax" },
    { value: 16, label: "VAT (16%)", name: "VAT" },
    { value: 8.5, label: "Sales Tax (8.5%)", name: "Sales Tax" },
    { value: 21, label: "Standard Rate (21%)", name: "Standard Rate" },
  ];

  // State for quote items
  const [items, setItems] = useState([]);

  // State for editing items
  const [newItem, setNewItem] = useState({
    productId: "",
    description: "",
    quantity: 1,
    rate: 0,
  });

  const [products, setProducts] = useState([]);
  const [packages, setPackages] = useState([]);
  
  // State for coupons
  const [coupons, setCoupons] = useState([]);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [couponDiscount, setCouponDiscount] = useState(0);

  // State for product creation modal
  const [showProductModal, setShowProductModal] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: "",
    description: "",
    price: "",
    category: "",
    sku: "",
  });

  // State for package modals
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [showCreatePackageModal, setShowCreatePackageModal] = useState(false);
  const [selectedPackageProducts, setSelectedPackageProducts] = useState([]);
  const [newPackage, setNewPackage] = useState({
    name: "",
    description: "",
    products: [],
    totalPrice: 0,
    category: "",
  });

  // State for create event modal
  const [showCreateEventModal, setShowCreateEventModal] = useState(false);
  const [creatingEvent, setCreatingEvent] = useState(false);
  const [eventName, setEventName] = useState("");
  const [eventTime, setEventTime] = useState("");
  const [paymentPlan, setPaymentPlan] = useState(() => emptyPaymentPlan());

  // visibility states
  const [showQuoteDatePicker, setShowQuoteDatePicker] = useState(false);
  const [showExpiryDatePicker, setShowExpiryDatePicker] = useState(false);
  const [showEventDatePicker, setShowEventDatePicker] = useState(false);
  const [showQuoteNumberEdit, setShowQuoteNumberEdit] = useState(false);

  const handleCalendar = (value) => {
    switch (value) {
      case "quote":
        return setShowQuoteDatePicker(!showQuoteDatePicker);
      case "expiry":
        return setShowExpiryDatePicker(!showExpiryDatePicker);
      case "event":
        return setShowEventDatePicker(!showEventDatePicker);
      default:
        return setShowQuoteDatePicker(!showQuoteDatePicker);
    }
  };

  const handleDateChange = (value, date) => {
    switch (value) {
      case "quote":
        return setQuote({
          ...quote,
          quoteDate: date,
          expiryDate: date,
        });
      case "expiry":
        return setQuote({
          ...quote,
          expiryDate: date,
        });
      case "event":
        return setQuote({
          ...quote,
          eventData: { ...quote.eventData, eventDate: date },
        });
      default:
        return setShowQuoteDatePicker(!showQuoteDatePicker);
    }
  };

  const handleTaxChange = (value) => {
    const selectedTax = taxOptions.find(
      (tax) => tax.value === parseFloat(value)
    );
    setQuote({
      ...quote,
      taxRate: parseFloat(value),
      taxName: selectedTax ? selectedTax.name : "Custom Tax",
    });
  };

  const handleEventDataChange = (field, value) => {
    setQuote({
      ...quote,
      eventData: {
        ...quote.eventData,
        [field]: value,
      },
    });
  };

  // Calculate totals first (before useEffects)
  const pendingItem = newItem.description?.trim()
    ? { ...newItem, amount: Number(newItem.quantity || 0) * Number(newItem.rate || 0) }
    : null;
  const effectiveItems = pendingItem ? [...items, pendingItem] : items;
  const subtotal = effectiveItems.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const subtotalAfterCoupon = Math.max(0, subtotal - couponDiscount);
  const taxAmount = (subtotalAfterCoupon * quote.taxRate) / 100;
  const total = subtotalAfterCoupon + taxAmount;

  // Fetch quote data on component mount
  useEffect(() => {
    if (isNewQuote) {
      generateQuoteNumber();
      fetchCustomers();
      fetchProducts();
      fetchPackages();
      fetchCoupons();
    } else {
      fetchQuoteData();
    }
  }, [id]);

  // Restore selected coupon when coupons are loaded
  useEffect(() => {
    if (quote.couponId && coupons.length > 0) {
      const savedCoupon = coupons.find(c => c._id === quote.couponId);
      if (savedCoupon && !selectedCoupon) {
        setSelectedCoupon(savedCoupon);
      }
    }
  }, [coupons, quote.couponId]);

  // Recalculate coupon discount when subtotal or selected coupon changes
  useEffect(() => {
    if (selectedCoupon && subtotal > 0) {
      const discount = (subtotal * selectedCoupon.discount) / 100;
      setCouponDiscount(discount);
      setQuote(prev => ({
        ...prev,
        couponDiscount: discount
      }));
    }
  }, [selectedCoupon, subtotal]);

  // Fetch customers for dropdown
  const fetchCustomers = async () => {
    try {
      const response = await http.get("/api/customers");
      setCustomers(response.data);
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await http.get("/api/products");
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const fetchPackages = async () => {
    try {
      const response = await http.get("/api/packages");
      setPackages(response.data);
    } catch (error) {
      console.error("Error fetching packages:", error);
    }
  };

  const fetchCoupons = async () => {
    try {
      const response = await http.get("/api/discounts");
      setCoupons(response.data);
    } catch (error) {
      console.error("Error fetching coupons:", error);
    }
  };

  // Generate new quote number
  const generateQuoteNumber = async () => {
    try {
      const response = await http.post("/api/quotes/generate-number");
      setQuote((prev) => ({
        ...prev,
        quoteNumber: response.data.quoteNumber,
      }));
    } catch (error) {
      console.error("Error generating quote number:", error);
      const year = new Date().getFullYear();
      const timestamp = Date.now().toString().slice(-3);
      setQuote((prev) => ({
        ...prev,
        quoteNumber: `QUO-${year}-${timestamp}`,
      }));
    }
  };

  // Fetch quote data for editing
  const fetchQuoteData = async () => {
    try {
      setLoading(true);

      // Fetch quote data and other required data in parallel
      const [quoteResponse] = await Promise.all([
        http.get(`/api/quotes/${id}?populate=customerId`),
        fetchCustomers(),
        fetchProducts(),
        fetchPackages(),
        fetchCoupons()
      ]);
      console.log("Quote data fetched:", quoteResponse.data);
      const quoteData = quoteResponse.data;

      setQuote(prev => ({
        ...prev,
        quoteNumber: quoteData.quoteNumber || "",
        quoteDate: quoteData.date?.split("T")[0] || "",
        expiryDate: quoteData.expiryDate?.split("T")[0] || "",
        customerId: quoteData.customerId?._id || quoteData.customerId || "",
        customerName: quoteData.customerId?.name || "",
        status: quoteData.status || "draft",
        total: quoteData.total || 0,
        subtotal: quoteData.cost || 0,
        notes: quoteData.description || "",
        terms: "",
        eventId: quoteData.eventId?._id || "",
        eventName: quoteData.eventId?.name || "",
        customFields: quoteData.customFields || {},
        template: quoteData.template || "villa",
        eventData: {
          ...prev.eventData,
          eventDate: quoteData.date?.split("T")[0] || "",
          eventTime: quoteData.time || "",
          eventType: quoteData.eventType || "",
          guests: quoteData.guests?.toString() || "",
          venue: quoteData.venue || quoteData.location || "",
        },
      }));

      setItems(quoteData.products || []);

      // Restore coupon if it exists
      if (quoteData.couponId && quoteData.couponDiscount) {
        setCouponDiscount(quoteData.couponDiscount);
      }
    } catch (error) {
      console.error("Error fetching quote data:", error);
      alert("Error loading quote data");
    } finally {
      setLoading(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount || 0);
  };

  // Handle coupon selection
  const handleCouponSelect = (couponId) => {
    if (!couponId) {
      setSelectedCoupon(null);
      setCouponDiscount(0);
      setQuote(prev => ({
        ...prev,
        couponId: "",
        couponDiscount: 0
      }));
      return;
    }

    const coupon = coupons.find((c) => c._id === couponId);
    if (coupon) {
      setSelectedCoupon(coupon);
      // Calculate discount based on subtotal and coupon percentage
      const discount = (subtotal * coupon.discount) / 100;
      setCouponDiscount(discount);
      setQuote(prev => ({
        ...prev,
        couponId: coupon._id,
        couponDiscount: discount
      }));
    }
  };

  // Handle product selection for existing items
  const handleProductSelect = (index, productId) => {
    const selectedProduct = products.find((p) => p._id === productId);
    if (selectedProduct) {
      updateItem(index, "productId", productId);
      updateItem(index, "description", selectedProduct.name);
      updateItem(index, "rate", selectedProduct.price);
    } else {
      updateItem(index, "productId", "");
    }
  };

  // Handle product selection for new item
  const handleNewItemProductSelect = (productId) => {
    if (productId === "add_new") {
      setShowProductModal(true);
      return;
    }

    const selectedProduct = products.find((p) => p._id === productId);
    if (selectedProduct) {
      setNewItem({
        ...newItem,
        productId: productId,
        description: selectedProduct.name,
        rate: selectedProduct.price,
      });
    } else {
      setNewItem({
        ...newItem,
        productId: "",
        description: "",
        rate: 0,
      });
    }
  };

  // Handle item updates
  const updateItem = (index, field, value) => {
    setItems(
      items.map((item, i) => {
        if (i === index) {
          const updatedItem = { ...item, [field]: value };
          if (field === "quantity" || field === "rate") {
            updatedItem.amount = updatedItem.quantity * updatedItem.rate;
          }
          return updatedItem;
        }
        return item;
      })
    );
  };

  // Add new item
  const addItem = () => {
    if (newItem.description) {
      const item = {
        ...newItem,
        amount: newItem.quantity * newItem.rate,
      };
      setItems([...items, item]);
      setNewItem({ productId: "", description: "", quantity: 1, rate: 0 });
    }
  };

  // Delete item
  const deleteItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  // Package functions
  const addPackage = () => {
    setShowPackageModal(true);
  };

  const handlePackageSelect = (packageData) => {
    if (!packageData.products || packageData.products.length === 0) {
      alert("This package has no products defined");
      return;
    }

    const itemNames = packageData.products
      .map((p) => {
        const product = p.product || (typeof p.productId === 'object' ? p.productId : null);
        return product?.name || "Unknown Item";
      })
      .join(", ");
    const packageDescription = `${packageData.name} - Includes: ${itemNames}`;

    const packagePrice =
      packageData.price ?? packageData.totalPrice ??
      packageData.products.reduce((sum, p) => {
        const product = p.product || (typeof p.productId === 'object' ? p.productId : null);
        return sum + (product ? product.price * p.quantity : 0);
      }, 0);

    const packageItem = {
      productId: null,
      packageId: packageData._id,
      description: packageDescription,
      quantity: 1,
      rate: packagePrice,
      amount: packagePrice,
      isPackage: true,
    };

    setItems([...items, packageItem]);
    setShowPackageModal(false);
  };

  const toggleProductInPackage = (productId) => {
    const isSelected = selectedPackageProducts.some(
      (p) => p.productId === productId
    );

    if (isSelected) {
      setSelectedPackageProducts(
        selectedPackageProducts.filter((p) => p.productId !== productId)
      );
    } else {
      const product = products.find((p) => p._id === productId);
      if (product) {
        setSelectedPackageProducts([
          ...selectedPackageProducts,
          {
            productId: productId,
            product: product,
            quantity: 1,
          },
        ]);
      }
    }
  };

  const updatePackageProductQuantity = (productId, quantity) => {
    setSelectedPackageProducts(
      selectedPackageProducts.map((p) =>
        p.productId === productId
          ? { ...p, quantity: parseInt(quantity) || 1 }
          : p
      )
    );
  };

  const calculatePackageTotal = () => {
    return selectedPackageProducts.reduce(
      (sum, p) => sum + p.product.price * p.quantity,
      0
    );
  };

  const createPackage = async (event) => {
    event?.preventDefault();
    if (!newPackage.name || selectedPackageProducts.length === 0) {
      alert("Please enter a package name and select at least one product");
      return;
    }

    try {
      const packageData = {
        ...newPackage,
        products: selectedPackageProducts,
        price: calculatePackageTotal(),
      };

      const response = await http.post("/api/packages", packageData);

      setPackages([...packages, {
        ...response.data,
        products: selectedPackageProducts,
        price: response.data.price ?? calculatePackageTotal(),
      }]);

      setNewPackage({
        name: "",
        description: "",
        products: [],
        totalPrice: 0,
        category: "",
      });
      setSelectedPackageProducts([]);
      setShowCreatePackageModal(false);

      alert("Package created successfully!");
    } catch (error) {
      console.error("Error creating package:", error);
      alert(
        "Error creating package: " +
          (error.response?.data?.error || error.response?.data?.message || error.message)
      );
    }
  };

  // Product creation functions
  const createProduct = async (event) => {
    event?.preventDefault();
    if (!newProduct.name || !newProduct.price) {
      alert("Please fill in required fields (name and price)");
      return;
    }

    try {
      const productData = {
        ...newProduct,
        price: parseFloat(newProduct.price),
      };

      const response = await http.post("/api/products", productData);

      setProducts([...products, response.data]);

      setNewItem({
        ...newItem,
        productId: response.data._id,
        description: response.data.name,
        rate: response.data.price,
      });

      setNewProduct({
        name: "",
        description: "",
        price: "",
        category: "",
        sku: "",
      });
      setShowProductModal(false);

      alert("Product created successfully!");
    } catch (error) {
      console.error("Error creating product:", error);
      alert(
        "Error creating product: " +
          (error.response?.data?.message || error.message)
      );
    }
  };

  // Save quote function
  const saveQuote = async () => {
    if (!quote.customerId) {
      alert("Please select a customer");
      return;
    }
    // Validation for required event fields
    if (!quote.eventData.eventDate) {
      alert("Please enter the event date");
      return;
    }
    if (!quote.eventData.eventType) {
      alert("Please enter the event type");
      return;
    }
    if (!isRealVenueName(quote.eventData.venue)) {
      alert("Please select a venue");
      return;
    }

    try {
      setSaving(true);

      const quoteData = {
        quoteNumber: quote.quoteNumber,
        expiryDate: quote.expiryDate || undefined,
        description: quote.notes || "", // Using notes as description
        date: quote.eventData.eventDate || new Date(),
        time: quote.eventData.eventTime || "",
        total: total,
        eventId: quote.eventId || undefined,
        eventName: quote.eventName || undefined,
        products: effectiveItems.map((item) => ({
          productId: item.productId || null,
          packageId: item.packageId || null,
          description: item.description,
          quantity: item.quantity,
          rate: item.rate,
          amount: item.amount,
          isPackage: item.isPackage || false,
        })),
        status: quote.status,
        cost: subtotal,
        guests: parseInt(quote.eventData.guests) || 0,
        eventType: quote.eventData.eventType,
        venue: quote.eventData.venue,
        location: quote.eventData.venue,
        customerId: quote.customerId,
        customFields: quote.customFields || {},
        template: quote.template || "villa",
      };

      let response;
      if (isNewQuote) {
        response = await http.post("/api/quotes", quoteData);
        navigate(`/quotes/${response.data._id}`);
      } else {
        response = await http.put(`/api/quotes/${id}`, quoteData);
        // Map the backend response back to frontend structure
        const savedQuoteData = response.data;
        setQuote(prev => ({
          ...prev,
          quoteNumber: savedQuoteData.quoteNumber || prev.quoteNumber,
          quoteDate: savedQuoteData.date?.split("T")[0] || prev.quoteDate,
          customerId: savedQuoteData.customerId?._id || savedQuoteData.customerId || prev.customerId,
          customerName: savedQuoteData.customerId?.name || prev.customerName,
          status: savedQuoteData.status || prev.status,
          total: savedQuoteData.total || prev.total,
          subtotal: savedQuoteData.cost || prev.subtotal,
          notes: savedQuoteData.description || prev.notes,
          customFields: savedQuoteData.customFields || prev.customFields,
          template: savedQuoteData.template || prev.template,
          eventData: {
            ...prev.eventData,
            eventDate: savedQuoteData.date?.split("T")[0] || prev.eventData.eventDate,
            eventTime: savedQuoteData.time || prev.eventData.eventTime,
            eventType: savedQuoteData.eventType || prev.eventData.eventType,
            guests: savedQuoteData.guests?.toString() || prev.eventData.guests,
            venue: savedQuoteData.venue || savedQuoteData.location || prev.eventData.venue,
          },
        }));
      }

      if (pendingItem) {
        setItems(effectiveItems);
        setNewItem({ productId: "", description: "", quantity: 1, rate: 0 });
      }

      alert("Quote saved successfully!");
    } catch (error) {
      console.error("Error saving quote:", error);
      alert(
        "Error saving quote: " +
          (error.userMessage || error.response?.data?.message || error.response?.data?.error || error.message)
      );
    } finally {
      setSaving(false);
    }
  };

  // Send quote
  const sendQuote = async () => {
    try {
      if (isNewQuote) {
        alert("Please save the quote first");
        return;
      }

      await http.post(`/api/quotes/${id}/send`);
      setQuote((prev) => ({ ...prev, status: "sent" }));
      alert("Quote sent successfully!");
    } catch (error) {
      console.error("Error sending quote:", error);
      alert("Error sending quote");
    }
  };

  // Generate proposal function
  const generateProposal = async (template) => {
    if (isNewQuote) {
      alert("Please save the quote first");
      return;
    }

    setGenerating(true);
    try {
      await http.put(`/api/quotes/${id}`, {
        template,
      });
      setQuote((prev) => ({ ...prev, template }));
      setShowTemplateModal(false);
      window.open(
        `/quotes/${id}/preview?template=${template}`,
        "_blank"
      );
    } catch (error) {
      console.error("Error generating proposal:", error);
      alert(
        "Error generating proposal: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setGenerating(false);
    }
  };

  // Create event from quote
  const createEventFromQuote = async () => {
    // Validation
    if (!eventName || eventName.trim() === "") {
      alert("Please enter the event name");
      return;
    }
    if (!quote.eventData.eventDate) {
      alert("Please enter the event date");
      return;
    }
    if (!quote.eventData.eventType) {
      alert("Please enter the event type");
      return;
    }
    if (!isRealVenueName(quote.eventData.venue)) {
      alert("Please select a venue");
      return;
    }
    if (!quote.customerId) {
      alert("Please select a customer");
      return;
    }
    const planError = validatePaymentPlan(paymentPlan);
    if (planError) {
      alert(planError);
      return;
    }

    try {
      setCreatingEvent(true);

      const eventData = {
        name: eventName || `${quote.eventData.eventType} Event`,
        date: quote.eventData.eventDate,
        time: eventTime || quote.eventData.eventTime || "",
        eventType: quote.eventData.eventType,
        guests: parseInt(quote.eventData.guests) || 0,
        amount: Number(paymentPlan.amount),
        venue: quote.eventData.venue,
        location: quote.eventData.venue,
        customerId: quote.customerId,
        description: quote.notes || "",
        customFields: quote.customFields || {},
        status: "pending",
        products: effectiveItems.map((item) => ({
          productId: item.productId || null,
          packageId: item.packageId || null,
          description: item.description,
          quantity: item.quantity,
          rate: item.rate,
          amount: item.amount,
          isPackage: item.isPackage || false,
        })),
        cost: subtotal,
        total: total,
        quoteId: id, // Link back to the quote
      };

      const response = await http.post("/api/events", eventData);
      const createdEventId = response.data?._id;

      if (!createdEventId) {
        throw new Error("The event was created but no event ID was returned");
      }

      await http.post(`/api/events/${createdEventId}/payments`, paymentPlanPayload(paymentPlan));
      
      // Update quote with eventId and eventName
      const updatedQuote = {
        ...quote,
        eventId: createdEventId,
        eventName: eventName,
      };
      
      // Save the updated quote with eventId
      try {
        await http.put(`/api/quotes/${id}`, {
          eventId: createdEventId,
          eventName: eventName,
        });
        setQuote(updatedQuote);
      } catch (updateError) {
        console.error("Error updating quote with eventId:", updateError);
      }
      
      setShowCreateEventModal(false);
      navigate(`/events/${createdEventId}`, { replace: true });
    } catch (error) {
      console.error("Error creating event:", error);
      alert(
        "Error creating event: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setCreatingEvent(false);
    }
  };

  // Handle navigation actions
  const handleBack = () => navigate("/quotes");
  const handlePreview = () => {
    window.open(`/quotes/${id}/preview?template=${quote.template || "villa"}`, "_blank");
  };
  const handleSend = sendQuote;
  const handleSave = saveQuote;

  // Show loading spinner while fetching data
  if (loading) {
    return (
      <div className="min-h-screen bg-purple-50 flex items-center justify-center">
        <div className="text-purple-600">Loading quote...</div>
      </div>
    );
  }

  return (
    <PageHeader title="Edit Quote" backPath="/quotes" compact>
      <div className="min-h-screen bg-[#F8F7FC]">
        {/* Main Content */}
        <div className="mx-auto max-w-[1600px] px-4 py-3 lg:px-6">
          {/* Quote Details */}
          <div className="hidden">
            <Link to="/">
              <HomeIcon />
            </Link>
            <div>
              <RightIcon color="#D4D4D5" />
            </div>
            <Link
              to="/quotes"
              className="block text-sm font-medium text-textLight"
            >
              Quotes
            </Link>
            <div>
              <RightIcon color="#D4D4D5" />
            </div>
            <p className="text-textDark font-medium text-sm">
              {quote?.quoteNumber}
            </p>
          </div>

          <div className="flex gap-2 items-center">
            {showQuoteNumberEdit ? (
              <input
                type="text"
                value={quote.quoteNumber}
                onChange={(e) =>
                  setQuote({
                    ...quote,
                    quoteNumber: e.target.value,
                  })
                }
                className="w-[200px] text-[26px] border border-gray-300 rounded p-0 focus:outline-none focus:ring-1 focus:ring-purple-500 border-0 font-medium"
              />
            ) : (
              <h2 className="text-[28px] leading-none text-[#15112B] font-semibold tracking-tight">
                {quote?.quoteNumber}
              </h2>
            )}

            <button
              className="rounded-lg border border-[#E5E1EF] bg-white p-1.5 text-gray-500 shadow-sm transition hover:border-purple-200 hover:text-purple-700"
              onClick={() => setShowQuoteNumberEdit(!showQuoteNumberEdit)}
            >
              <EditIcon color="#8E8C8F" />
            </button>
          </div>

          {/* actions */}
          <div className="mt-2 flex flex-row items-center justify-between gap-3 flex-wrap lg:-mt-9 lg:min-h-9 lg:pl-64">
            <div className="flex gap-5 flex-wrap rounded-xl text-sm">
              {/* issue date */}
              <div className="relative">
                <div
                  className="flex flex-row items-center gap-2 cursor-pointer"
                  onClick={() => handleCalendar("quote")}
                >
                  <CalenderIcon color="#8E8C8F" />
                  <p className="text-textLight text-sm">Issued on</p>
                  <p className="text-textDark font-medium text-sm">
                    {dayjs(quote?.quoteDate).format("DD MMM YYYY")}
                  </p>
                  {showQuoteDatePicker ? (
                    <span className="-rotate-180">
                      <ArrowDownIcon color="#240046" />
                    </span>
                  ) : (
                    <ArrowDownIcon color="#AEADAF" />
                  )}
                </div>

                {showQuoteDatePicker && (
                  <div className="absolute z-50 top-full mt-2 right-0 w-full">
                    <CustomDatePicker
                      showPresets={false}
                      selectedDate={quote?.quoteDate}
                      onChange={(date) => handleDateChange("quote", date)}
                      onClose={() => setShowQuoteDatePicker(false)}
                    />
                  </div>
                )}
              </div>
              {/* expiry date */}
              <div className="relative">
                <div
                  className="flex flex-row items-center gap-2 cursor-pointer"
                  onClick={() => handleCalendar("expiry")}
                >
                  <DateTimeIcon color="#8E8C8F" />
                  <p className="text-textLight text-sm">Valid Until</p>
                  <p className="text-textDark font-medium text-sm">
                    {dayjs(quote?.expiryDate).format("DD MMM YYYY")}
                  </p>
                  {showExpiryDatePicker ? (
                    <span className="-rotate-180">
                      <ArrowDownIcon color="#240046" />
                    </span>
                  ) : (
                    <ArrowDownIcon color="#AEADAF" />
                  )}
                </div>

                {showExpiryDatePicker && (
                  <div className="absolute z-50 top-full mt-2 right-0 w-full">
                    <CustomDatePicker
                      showPresets={false}
                      selectedDate={quote?.expiryDate}
                      onChange={(date) => handleDateChange("expiry", date)}
                      onClose={() => setShowExpiryDatePicker(false)}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={handlePreview}
                disabled={isNewQuote}
                className="flex h-9 bg-white items-center gap-2 px-3 text-sm text-gray-700 border border-[#E5E1EF] rounded-lg shadow-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                <Eye className="h-4 w-4 text-gray-500" />
                <span>Preview</span>
              </button>
              <button
                onClick={handleSend}
                disabled={isNewQuote || saving}
                className="flex h-9 bg-white items-center gap-2 px-3 text-sm text-gray-700 border border-[#E5E1EF] rounded-lg shadow-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                <span>
                  <SendIcon color="#8E8C8F" />
                </span>
                <span>Send</span>
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex h-9 bg-white items-center gap-2 px-3 text-sm text-gray-700 border border-[#E5E1EF] rounded-lg shadow-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                <SaveIcon />
                {saving ? "Saving..." : "Save"}
              </button>
              <button
                onClick={() => {
                  setSelectedTemplate(quote.template || "villa");
                  setShowTemplateModal(true);
                }}
                disabled={isNewQuote || generating}
                className="flex h-9 items-center gap-2 px-3 text-sm rounded-lg shadow-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold bg-emerald-600 text-white"
              >
                <FileText className="h-4 w-4" />
                {generating ? "Generating..." : "Generate Proposal"}
              </button>
              <button
                onClick={() => {
                  setEventName(`${quote.eventData.eventType || "Event"} - ${customers.find((c) => c._id === quote.customerId)?.name || "Customer"}`);
                  setEventTime(quote.eventData.eventTime || "");
                  setPaymentPlan(emptyPaymentPlan(total, quote.eventData.eventDate || today));
                  setShowCreateEventModal(true);
                }}
                disabled={isNewQuote}
                className="flex h-9 items-center gap-2 px-3 text-sm rounded-lg shadow-sm hover:bg-purple-800 disabled:opacity-50 disabled:cursor-not-allowed font-semibold bg-purple-600 text-white"
              >
                <FileText className="h-4 w-4" />
                Create Event
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 pt-3 xl:grid-cols-[minmax(0,1.8fr)_minmax(330px,0.7fr)]">
            {/* Left Column - Form Content */}
            <div className="flex min-w-0 flex-col gap-3">
              {/* Event Information - Now with input fields */}
              <div className="rounded-xl border border-[#E9E5F1] bg-white p-4 shadow-[0_6px_20px_rgba(37,24,68,0.04)]">
                <h3 className="text-base font-semibold text-[#17132B] flex gap-2 items-center mb-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-50 text-purple-600"><CalendarDays className="h-4 w-4" /></span>
                  Event Information
                </h3>

                <div className="grid grid-cols-1 gap-x-3 gap-y-3 md:grid-cols-2 lg:grid-cols-3">
                  {/* Event Date */}
                  <div className="relative">
                    <label className="block text-sm font-medium text-textDark mb-1">
                      Event Date <span className="text-red-500">*</span>
                    </label>
                    <div
                      className="flex h-10 flex-row items-center gap-2 cursor-pointer rounded-lg border border-[#DDD7E8] bg-white px-3 focus-within:ring-2 focus-within:ring-purple-100"
                      onClick={() => handleCalendar("event")}
                    >
                      <CalenderCheckIcon />
                      <input
                        type="text"
                        value={
                          quote.eventData.eventDate
                            ? dayjs(quote.eventData.eventDate).format(
                                "DD MMM YYYY"
                              )
                            : "Select date"
                        }
                        readOnly
                        className="flex-1 text-sm focus:outline-none cursor-pointer"
                      />
                      <ArrowDownIcon color="#AEADAF" />
                    </div>

                    {showEventDatePicker && (
                      <div className="absolute z-50 top-full mt-2 left-0 w-full">
                        <CustomDatePicker
                          showPresets={false}
                          selectedDate={quote.eventData.eventDate}
                          onChange={(date) => handleDateChange("event", date)}
                          onClose={() => setShowEventDatePicker(false)}
                        />
                      </div>
                    )}
                  </div>

                  {/* Event Type */}
                  <div>
                    <label className="block text-sm font-medium text-textDark mb-1">
                      Event Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={quote.eventData.eventType}
                      onChange={(e) =>
                        handleEventDataChange("eventType", e.target.value)
                      }
                      className="h-10 w-full rounded-lg border border-[#DDD7E8] bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-100"
                    >
                      <option value="">Select an event type</option>
                      {quote.eventData.eventType && !eventTypes.includes(quote.eventData.eventType) && (
                        <option value={quote.eventData.eventType}>{quote.eventData.eventType}</option>
                      )}
                      {eventTypes.map(type => <option key={type} value={type}>{type}</option>)}
                    </select>
                  </div>

                  {/* Number of Guests */}
                  <div>
                    <label className="block text-sm font-medium text-textDark mb-1">
                      Number of Guests
                    </label>
                    <input
                      type="number"
                      placeholder="0"
                      value={quote.eventData.guests}
                      onChange={(e) =>
                        handleEventDataChange("guests", e.target.value)
                      }
                      className="h-10 w-full rounded-lg border border-[#DDD7E8] bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-100"
                    />
                  </div>

                  {/* Venue */}
                  <div className="md:col-span-2 lg:col-span-3">
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Venue <span className="text-red-500">*</span>
                    </label>
                    <SearchableSelect value={isRealVenueName(quote.eventData.venue) ? quote.eventData.venue : ''} options={isRealVenueName(quote.eventData.venue) && !venueNames.includes(quote.eventData.venue) ? [quote.eventData.venue, ...venueNames] : venueNames} placeholder="Select a venue" searchPlaceholder="Search venues" onChange={venue => handleEventDataChange("venue", venue)} disabled={venueNames.length === 0 && !isRealVenueName(quote.eventData.venue)} />
                    {venueNames.length === 0 && !isRealVenueName(quote.eventData.venue) && <p className="mt-1.5 text-xs text-amber-600">Add a venue in Settings before creating this quote.</p>}
                  </div>

                </div>
                {quoteCustomFields.length > 0 && <div className="mt-3 border-t border-gray-100 pt-3"><h4 className="mb-3 text-sm font-medium text-gray-900">Custom Fields</h4><DynamicFieldsEditor fields={quoteCustomFields} module="event" values={quote.customFields} onChange={customFields => setQuote(previous => ({ ...previous, customFields }))} /></div>}
              </div>

              {/* Services & Items */}
              <div className="rounded-xl border border-[#E9E5F1] bg-white p-4 shadow-[0_6px_20px_rgba(37,24,68,0.04)]">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-medium text-textDark flex gap-2 items-center">
                    <ServiceIcon /> Services & Items
                  </h3>
                  <button
                    onClick={addPackage}
                    className="flex bg-[#FAF8FF] items-center gap-1.5 py-2 px-3.5 text-sm text-purple-700 border border-purple-100 rounded-lg hover:bg-purple-50 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  >
                    <Plus className="h-3 w-3 text-[#5C5C5C]" />
                    Package
                  </button>
                </div>

                <QuoteItemsTable
                  items={items}
                  newItem={newItem}
                  products={products}
                  formatCurrency={formatCurrency}
                  handleProductSelect={handleProductSelect}
                  updateItem={updateItem}
                  deleteItem={deleteItem}
                  handleNewItemProductSelect={handleNewItemProductSelect}
                  setNewItem={setNewItem}
                  addItem={addItem}
                />
              </div>

              {/* Additional Information */}

              {/* Additional Information */}
              <div className="rounded-xl border border-[#E9E5F1] bg-white p-4 shadow-[0_6px_20px_rgba(37,24,68,0.04)]">
                <h3 className="text-base font-medium text-textDark flex gap-2 items-center pb-3">
                  <AdditionalInfoIcon /> Additional Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="relative">
                    <label className="block text-sm font-medium text-textDark mb-1">
                      Notes <span className="text-[#C0BFC1]">(Optional)</span>
                    </label>

                    <textarea
                      value={quote.notes || ""}
                      onChange={(e) =>
                        setQuote({ ...quote, notes: e.target.value })
                      }
                      rows={3}
                      maxLength={200}
                      placeholder="Write any notes here..."
                      className="w-full border border-[#EBEBEB] rounded-lg px-[16px] py-[12px] text-sm focus:outline-none shadow-sm resize-y"
                    />

                    <div className="absolute bottom-3.5 right-8 text-xs text-gray-400">
                      {(quote.notes || "").length}/200
                    </div>

                    <div className="absolute bottom-4 right-4">
                      <ResizeIcon />
                    </div>
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-textDark mb-1">
                      Terms & Conditions{" "}
                      <span className="text-[#C0BFC1]">(Optional)</span>
                    </label>

                    <textarea
                      value={quote.terms || ""}
                      onChange={(e) =>
                        setQuote({ ...quote, terms: e.target.value })
                      }
                      rows={3}
                      maxLength={200}
                      placeholder="Write any notes here..."
                      className="w-full border border-[#EBEBEB] rounded-lg px-[16px] py-[12px] text-sm focus:outline-none shadow-sm resize-y"
                    />

                    <div className="absolute bottom-3.5 right-8 text-xs text-gray-400">
                      {(quote.terms || "").length}/200
                    </div>

                    <div className="absolute bottom-4 right-4">
                      <ResizeIcon />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Quote Summary */}
            <div className="space-y-3 xl:sticky xl:top-3 xl:self-start">
              <div className="rounded-xl border border-[#E9E5F1] bg-white p-4 shadow-[0_6px_20px_rgba(37,24,68,0.04)]">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-medium text-textDark flex gap-2 items-center">
                    <ContactInfoIcon /> Contact Information
                  </h3>
                  <button>
                    <LinkIcon />
                  </button>
                </div>

                <div className="space-y-3 text-sm">
                  <div>
                    <label className="block text-sm font-medium text-textDark mb-1">
                      Customer
                    </label>
                    <div className="relative w-full">
                      <select
                        value={quote.customerId}
                        onChange={(e) => {
                          if (e.target.value === "__add_customer__") {
                            setShowCustomerModal(true);
                            return;
                          }
                          const selectedCustomer = customers.find(
                            (c) => c._id === e.target.value
                          );
                          setQuote({
                            ...quote,
                            customerId: e.target.value,
                            customerName: selectedCustomer?.name || "",
                            customerEmail: selectedCustomer?.email || "",
                            billingAddress: selectedCustomer?.address || "",
                          });
                        }}
                      className="appearance-none h-10 w-full rounded-lg border border-[#DDD7E8] bg-white px-3 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-purple-100"
                        style={{ color: "#171717" }}
                      >
                        <option value="" className="text-gray-400">
                          Select Customer
                        </option>
                        <option value="__add_customer__">+ Add New Customer</option>
                        {customers.map((customer) => (
                          <option key={customer._id} value={customer._id}>
                            {customer.name}
                          </option>
                        ))}
                      </select>

                      <div className="pointer-events-none absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-textDark mb-1">
                      Associated Email
                    </label>
                    <div className="relative">
                      <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-purple-400" />
                      <input
                        type="email"
                        value={quote.customerEmail}
                        onChange={(e) =>
                          setQuote({
                            ...quote,
                            customerEmail: e.target.value,
                          })
                        }
                        className="h-10 w-full rounded-lg border border-[#DDD7E8] bg-white pl-10 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-100"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="overflow-hidden rounded-xl border border-[#E9E5F1] bg-white shadow-[0_6px_20px_rgba(37,24,68,0.04)]">
                <div className="p-4">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <h3 className="text-base font-medium text-textDark flex gap-2 items-center">
                      <InvoiceSummaryIcon /> Quote Summary
                    </h3>
                    <span className="rounded-full bg-purple-50 px-2.5 py-1 text-xs font-semibold capitalize text-purple-700">
                      {quote.status}
                    </span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span className="text-[#8E8C8F]">Subtotal:</span>
                    <span className="font-medium text-base">
                      {formatCurrency(subtotal)}
                    </span>
                  </div>
                  
                  {/* Coupon Section */}
                  <div className="flex justify-between items-center mb-2 gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-[#8E8C8F] text-base">Coupon</span>
                      <div className="relative w-[80%]">
                        <select
                          value={selectedCoupon?._id || ""}
                          onChange={(e) => handleCouponSelect(e.target.value)}
                          className="appearance-none w-full border border-[#EBEBEB] rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-0 focus:ring-[#EBEBEB] pr-10 shadow-sm"
                        >
                          <option value="">Select a coupon</option>
                          {coupons.map((coupon) => (
                            <option key={coupon._id} value={coupon._id}>
                              {coupon.name} ({coupon.discount}% off)
                            </option>
                          ))}
                        </select>
                        <div className="pointer-events-none absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                    <span className="font-medium min-w-16 text-right text-base text-green-600">
                      {couponDiscount > 0 ? `-${formatCurrency(couponDiscount)}` : formatCurrency(0)}
                    </span>
                  </div>
                  
                  {selectedCoupon && (
                    <div className="flex justify-between mb-2">
                      <span className="text-[#8E8C8F]">Subtotal (after discount):</span>
                      <span className="font-medium text-base">
                        {formatCurrency(subtotalAfterCoupon)}
                      </span>
                    </div>
                  )}
                  
                  <div className="flex justify-between items-center  gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-[#8E8C8F]  text-base">Tax </span>
                      <div className="relative w-[80%]">
                        <select
                          value={quote.taxRate}
                          onChange={(e) => handleTaxChange(e.target.value)}
                          className="appearance-none w-full border border-[#EBEBEB] rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-0 focus:ring-[#EBEBEB] pr-10 shadow-sm"
                        >
                          {taxOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <div className="pointer-events-none absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                    <span className="font-medium min-w-16 text-right text-base">
                      {formatCurrency(taxAmount)}
                    </span>
                  </div>
                </div>

                <div className="m-3 mt-1 flex justify-between items-center rounded-xl border border-purple-100 bg-gradient-to-br from-[#F7F2FF] to-[#F4F1FF] px-4 py-3">
                  <div>
                    <span className="text-base font-semibold text-[#18181B] block mb-1">
                      Total Amount:
                    </span>

                    <span className="text-xs text-[#8E8C8F] flex gap-2 items-center block">
                      {" "}
                      <ClockIcon /> Valid Until{" "}
                      {quote?.expiryDate &&
                        dayjs(quote?.expiryDate).format("D MMM YYYY")}
                    </span>
                  </div>
                  <span className="text-2xl font-bold tracking-tight text-purple-700">
                    {formatCurrency(total)}
                  </span>
                </div>
              </div>

              {/* Event Card */}
              {quote.eventId && (
                <div className="rounded-2xl border border-[#E9E5F1] bg-white p-5 shadow-[0_8px_30px_rgba(37,24,68,0.05)]">
                  <h3 className="text-lg font-medium text-textDark flex gap-2 items-center pb-[16px]">
                    <CalenderBigIcon /> Linked Event
                  </h3>

                  <div className="space-y-3 text-sm">
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Event Name:</span>
                        <span className="font-medium text-gray-800">
                          {quote.eventName || "Unnamed Event"}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Date:</span>
                        <span className="font-medium text-gray-800">
                          {quote.eventData.eventDate
                            ? dayjs(quote.eventData.eventDate).format("DD MMM YYYY")
                            : "Not set"}
                        </span>
                      </div>
                    </div>
                    
                    <Link
                      to={`/events/${quote.eventId}`}
                      className="flex items-center justify-center gap-2 w-full bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 transition-colors text-sm font-medium"
                    >
                      <FileText className="h-4 w-4" />
                      View Event Details
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Product Creation Modal */}
        {showProductModal && (
          <div
            className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setShowProductModal(false);
            }}
          >
            <form
              onSubmit={createProduct}
              className="w-full max-w-lg rounded-xl bg-white p-5 shadow-2xl"
            >
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Add New Product</h3>
                  <p className="mt-0.5 text-xs text-gray-500">The new product will be selected in this quote.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowProductModal(false)}
                  className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                  aria-label="Close product form"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-sm font-medium text-gray-700">Product Name *</label>
                  <input
                    autoFocus
                    required
                    value={newProduct.name}
                    onChange={(event) => setNewProduct({ ...newProduct, name: event.target.value })}
                    className="h-10 w-full rounded-lg border border-gray-300 px-3 text-sm focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-100"
                    placeholder="Product name"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Price (MXN) *</label>
                  <input
                    required
                    type="number"
                    min="0"
                    step="0.01"
                    value={newProduct.price}
                    onChange={(event) => setNewProduct({ ...newProduct, price: event.target.value })}
                    className="h-10 w-full rounded-lg border border-gray-300 px-3 text-sm focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-100"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">SKU</label>
                  <input
                    value={newProduct.sku}
                    onChange={(event) => setNewProduct({ ...newProduct, sku: event.target.value })}
                    className="h-10 w-full rounded-lg border border-gray-300 px-3 text-sm focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-100"
                    placeholder="Optional SKU"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-sm font-medium text-gray-700">Category</label>
                  <select
                    value={newProduct.category}
                    onChange={(event) => setNewProduct({ ...newProduct, category: event.target.value })}
                    className="h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-100"
                  >
                    <option value="">Select a category</option>
                    <option value="Decorations">Decorations</option>
                    <option value="Furniture">Furniture</option>
                    <option value="Lighting">Lighting</option>
                    <option value="Audio/Visual">Audio/Visual</option>
                    <option value="Catering Supplies">Catering Supplies</option>
                    <option value="Linens">Linens</option>
                    <option value="Florals">Florals</option>
                    <option value="Entertainment">Entertainment</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    rows={3}
                    value={newProduct.description}
                    onChange={(event) => setNewProduct({ ...newProduct, description: event.target.value })}
                    className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-100"
                    placeholder="Optional product description"
                  />
                </div>
              </div>

              <div className="mt-5 flex justify-end gap-2 border-t border-gray-100 pt-4">
                <button
                  type="button"
                  onClick={() => setShowProductModal(false)}
                  className="h-10 rounded-lg border border-gray-300 px-4 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="h-10 rounded-lg bg-purple-600 px-4 text-sm font-semibold text-white hover:bg-purple-700"
                >
                  Create Product
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Package Selection Modal */}
        {showPackageModal && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowPackageModal(false);
              }
            }}
          >
            <div className="bg-white rounded-lg p-4 w-full max-w-2xl mx-4 relative max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-base font-semibold text-gray-800">
                  Select Package
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowCreatePackageModal(true)}
                    className="text-sm bg-purple-600 text-white px-3 py-1 rounded hover:bg-purple-700"
                  >
                    Create New Package
                  </button>
                  <button
                    onClick={() => setShowPackageModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                {packages.length === 0 ? (
                  <div className="text-center py-8">
                    <Package className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">
                      No packages available
                    </p>
                    <button
                      onClick={() => setShowCreatePackageModal(true)}
                      className="mt-2 text-purple-600 hover:text-purple-700 text-sm"
                    >
                      Create your first package
                    </button>
                  </div>
                ) : (
                  packages.map((pkg) => (
                    <div
                      key={pkg._id}
                      className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 cursor-pointer"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-800">
                            {pkg.name}
                          </h4>
                          {pkg.description && (
                            <p className="text-sm text-gray-600 mt-1">
                              {pkg.description}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="font-semibold text-purple-600">
                            {formatCurrency(pkg.price ?? pkg.totalPrice ?? 0)}
                          </div>
                          {pkg.category && (
                            <div className="text-xs text-gray-500">
                              {pkg.category}
                            </div>
                          )}
                        </div>
                      </div>

                      {pkg.products && pkg.products.length > 0 && (
                        <div className="text-xs text-gray-600 mb-2">
                          <strong>Includes:</strong>{" "}
                          {pkg.products
                            .map(
                              (p) =>
                                `${p.quantity}x ${
                                  p.product ? p.product.name : "Unknown"
                                }`
                            )
                            .join(", ")}
                        </div>
                      )}

                      <button
                        onClick={() => handlePackageSelect(pkg)}
                        className="w-full bg-purple-600 text-white px-3 py-1.5 text-sm rounded hover:bg-purple-700"
                      >
                        Add Package
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Package Creation Modal */}
        {showCreatePackageModal && (
          <div
            className="fixed inset-0 z-[11000] flex items-center justify-center bg-black/55 p-4"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setShowCreatePackageModal(false);
            }}
          >
            <form
              onSubmit={createPackage}
              className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
            >
              <div className="flex items-start justify-between border-b border-gray-100 px-5 py-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Create Package</h3>
                  <p className="mt-0.5 text-xs text-gray-500">Combine products and choose the quantity of each item.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowCreatePackageModal(false)}
                  className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                  aria-label="Close package form"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="overflow-y-auto p-5">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Package Name *</label>
                    <input
                      autoFocus
                      required
                      value={newPackage.name}
                      onChange={(event) => setNewPackage({ ...newPackage, name: event.target.value })}
                      className="h-10 w-full rounded-lg border border-gray-300 px-3 text-sm focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-100"
                      placeholder="Package name"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Category</label>
                    <input
                      value={newPackage.category}
                      onChange={(event) => setNewPackage({ ...newPackage, category: event.target.value })}
                      className="h-10 w-full rounded-lg border border-gray-300 px-3 text-sm focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-100"
                      placeholder="Optional category"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="mb-1 block text-sm font-medium text-gray-700">Description</label>
                    <textarea
                      rows={2}
                      value={newPackage.description}
                      onChange={(event) => setNewPackage({ ...newPackage, description: event.target.value })}
                      className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-100"
                      placeholder="Optional package description"
                    />
                  </div>
                </div>

                <div className="mt-5">
                  <div className="mb-2 flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-gray-900">Products *</h4>
                    <span className="text-xs text-gray-500">{selectedPackageProducts.length} selected</span>
                  </div>
                  <div className="max-h-64 overflow-y-auto rounded-lg border border-gray-200">
                    {products.length === 0 ? (
                      <p className="px-4 py-8 text-center text-sm text-gray-500">Create a product before creating a package.</p>
                    ) : products.map((product) => {
                      const selected = selectedPackageProducts.find(item => item.productId === product._id);
                      return (
                        <div key={product._id} className="flex items-center gap-3 border-b border-gray-100 px-3 py-2.5 last:border-b-0 hover:bg-gray-50">
                          <input
                            type="checkbox"
                            checked={Boolean(selected)}
                            onChange={() => toggleProductInPackage(product._id)}
                            className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          />
                          <button type="button" onClick={() => toggleProductInPackage(product._id)} className="min-w-0 flex-1 text-left">
                            <span className="block truncate text-sm font-medium text-gray-900">{product.name}</span>
                            <span className="text-xs text-gray-500">{formatCurrency(product.price)}</span>
                          </button>
                          {selected && (
                            <label className="flex items-center gap-2 text-xs text-gray-500">
                              Qty
                              <input
                                type="number"
                                min="1"
                                value={selected.quantity}
                                onChange={(event) => updatePackageProductQuantity(product._id, event.target.value)}
                                className="h-8 w-16 rounded border border-gray-300 px-2 text-center text-sm focus:border-purple-500 focus:outline-none"
                              />
                            </label>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between rounded-lg bg-purple-50 px-4 py-3">
                  <span className="text-sm font-medium text-gray-700">Package total</span>
                  <span className="text-lg font-bold text-purple-700">{formatCurrency(calculatePackageTotal())}</span>
                </div>
              </div>

              <div className="flex justify-end gap-2 border-t border-gray-100 px-5 py-4">
                <button
                  type="button"
                  onClick={() => setShowCreatePackageModal(false)}
                  className="h-10 rounded-lg border border-gray-300 px-4 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="h-10 rounded-lg bg-purple-600 px-4 text-sm font-semibold text-white hover:bg-purple-700"
                >
                  Create Package
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Proposal Template Modal */}
        {showTemplateModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={(event) => {
              if (event.target === event.currentTarget && !generating) setShowTemplateModal(false);
            }}
          >
            <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
              <div className="mb-5 flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Select proposal template</h3>
                  <p className="mt-1 text-sm text-gray-500">Choose the design to use for this proposal.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowTemplateModal(false)}
                  disabled={generating}
                  className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:cursor-not-allowed"
                  aria-label="Close template selection"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {[
                  { value: "villa", name: "Villa", description: "Elegant" },
                  { value: "nexo", name: "Nexo", description: "Modern" },
                ].map((template) => (
                  <button
                    key={template.value}
                    type="button"
                    onClick={() => setSelectedTemplate(template.value)}
                    className={`rounded-lg border p-4 text-left transition-colors ${
                      selectedTemplate === template.value
                        ? "border-purple-600 bg-purple-50 ring-1 ring-purple-600"
                        : "border-gray-200 hover:border-purple-300 hover:bg-gray-50"
                    }`}
                  >
                    <span className="block font-semibold text-gray-900">{template.name}</span>
                    <span className="mt-1 block text-sm text-gray-500">{template.description}</span>
                  </button>
                ))}
              </div>

              <div className="mt-6 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowTemplateModal(false)}
                  disabled={generating}
                  className="h-10 rounded border border-gray-200 px-4 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => generateProposal(selectedTemplate)}
                  disabled={generating}
                  className="h-10 rounded bg-green-600 px-4 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {generating ? "Generating..." : "Generate Proposal"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create Event Modal */}
        {showCreateEventModal && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowCreateEventModal(false);
              }
            }}
          >
            <div className="relative mx-4 max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-xl bg-white p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800">
                  Create Event from Quote
                </h3>
                <button
                  onClick={() => setShowCreateEventModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Event Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={eventName}
                      onChange={(e) => setEventName(e.target.value)}
                      placeholder="Enter event name..."
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-purple-900 mb-2">
                    Event Summary
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Customer:</span>
                      <span className="font-medium text-gray-800">
                        {customers.find((c) => c._id === quote.customerId)
                          ?.name || "Not selected"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Date:</span>
                      <span className="font-medium text-gray-800">
                        {quote.eventData.eventDate
                          ? dayjs(quote.eventData.eventDate).format(
                              "DD MMM YYYY"
                            )
                          : "Not set"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Type:</span>
                      <span className="font-medium text-gray-800">
                        {quote.eventData.eventType || "Not specified"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Venue:</span>
                      <span className="font-medium text-gray-800">
                        {quote.eventData.venue || "Not specified"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Guests:</span>
                      <span className="font-medium text-gray-800">
                        {quote.eventData.guests || "0"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Items:</span>
                      <span className="font-medium text-gray-800">
                        {items.length} product(s)
                      </span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-purple-200">
                      <span className="text-gray-800 font-semibold">Total:</span>
                      <span className="font-bold text-purple-900">
                        {formatCurrency(total)}
                      </span>
                    </div>
                  </div>
                </div>

                <PaymentPlanEditor value={paymentPlan} onChange={setPaymentPlan} lockTotal />

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-yellow-800">
                    <strong>Note:</strong> This will create a new event with all
                    the information from this quote and create its payment plan.
                    Make sure the scheduled payments equal the quote total.
                  </p>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setShowCreateEventModal(false)}
                    className="flex-1 px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={createEventFromQuote}
                    disabled={creatingEvent}
                    className="flex-1 px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  >
                    {creatingEvent ? "Creating..." : "Create Event"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        <AddCustomerModal
          open={showCustomerModal}
          onClose={() => setShowCustomerModal(false)}
          onCreated={(customer) => {
            setCustomers(previous => [...previous, customer]);
            setQuote(previous => ({
              ...previous,
              customerId: customer._id,
              customerName: customer.name || "",
              customerEmail: customer.email || "",
              billingAddress: customer.address || "",
            }));
          }}
        />
      </div>
    </PageHeader>
  );
}
